import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import xss from 'xss';

import AdServiceTypeDisplay, { DotColor } from '@Components/AdServiceTypeDisplay';
import AdFavoriteButton from '@Components/Advert/AdFavoriteButton';
import FeaturedAdverts from '@Components/Advert/FeaturedAdverts';
import LastPreviewedAdverts from '@Components/Advert/LastPreviewedAdverts';
import RelatedAdverts from '@Components/Advert/RelatedAdverts';
import Breadcrumb from '@Components/Breadcrumb';
import Button, { ButtonSizes, ButtonStyles } from '@Components/Buttons/Button';
import ButtonLink from '@Components/Buttons/ButtonLink';
import CompanyNameWithRating from '@Components/Company/CompanyNameWithRating';
import DisplayLabelWithIcon from '@Components/DisplayLabelWithIcon';
import Icon, { IconSizes } from '@Components/Icon';
import Image from '@Components/Image';
import ImageGallery from '@Components/ImageGallery';
import Col from '@Components/layout/Col';
import ElementsRow from '@Components/layout/ElementsRow/ElementsRow';
import GridCol from '@Components/layout/Grid/GridCol';
import GridRow from '@Components/layout/Grid/GridRow';
import Row from '@Components/layout/Row';
import PageTitle from '@Components/Page/PageTitle';
import Paper, { PaperWidth } from '@Components/Paper';
import PaperBody, { PaperBodySpacing } from '@Components/PaperBody';
import PriceDisplay from '@Components/PriceDisplay';
import { useAppKindContext } from '@Components/root';
import PageSection from '@Components/sections/PageSection';
import AdvertSchema from '@Components/seo/schema/AdvertSchema';
import Typography from '@Components/Typography/Typography';
import UserAvatar from '@Components/UserAvatar';
import config from '@Config/config';
import { UserTourStates, UserTourSteps, USER_TOURS } from '@Config/constants';
import { Messages } from '@Config/messages';
import { RouteConfig } from '@Config/routes';
import {
  AdvertLoanMutation,
  AdvertStatus,
  LoadAdForDisplayQuery,
  useAdvertLoanMutation,
  useAdvertViewCountMutation,
} from '@Graphql/graphqlTypes.generated';
import { usePhoneNumber } from '@Hooks/usePhoneNumber';
import { useSetAppKind } from '@Hooks/useSetAppKind';
import { useTranslateObject } from '@Hooks/useTranslateObject';
import { useTranslations } from '@Hooks/useTranslations';
import PlaceholderImage from '@Images/placeholder.png';
import { showUserTour } from '@Store/app/app.actions';
import { getAppConfig } from '@Store/app/app.selectors';
import { getUserIsLogged } from '@Store/auth/auth.selector';
import { buildAdvertBreadCrumbLinks } from '@Utils/breadcrumb';
import { callMutationForForm } from '@Utils/graphql';
import useAdvertHistory from '@Utils/hooks/useAdvertHistory';
import { getFromLocalStorage, setToLocalStorage } from '@Utils/localStorage';
import { getTotalLoanCost } from '@Utils/money';

import styles from './AdPageContainer.module.scss';
import AdPageOrderSection from './AdPageOrderSection';
import LoanForm from './LoanForm';
import { LoanFormValues } from './LoanForm/LoanForm';
import PropertiesTable from './PropertiesTable/PropertiesTable';

const AdPage: React.FunctionComponent<{ advert: LoadAdForDisplayQuery['advertBySlug'] }> = ({ advert }) => {
  const companyRoutePath = RouteConfig.Company.buildLink({ slug: advert.company.slug });
  const [isOrderSectionOpen, setIsOrderSectionOpen] = useState(false);
  const isLoggedIn = useSelector(getUserIsLogged);
  const t = useTranslations();
  const selectTranslation = useTranslateObject();
  const dispatch = useDispatch();
  const { appKind } = useAppKindContext();

  const isFirstLogin = !getFromLocalStorage(USER_TOURS.advertPageTour);
  const [updateAdvertViewsCount] = useAdvertViewCountMutation();
  const { addNewIdToHistory } = useAdvertHistory();
  const isAdExpired = advert.validUntil && new Date(advert.validUntil) < new Date();
  const sejicoContactPhone = usePhoneNumber();

  const contactPhone = advert.phone || sejicoContactPhone;
  const appConfig = useSelector(getAppConfig);

  const [createAdvertLoan, { data }] = useAdvertLoanMutation();

  const handleSubmit = (values: LoanFormValues) => {
    const isContactTypePhone = values.contactType === 'phone';
    const contactField = isContactTypePhone ? { phone: values.contactDetails } : { email: values.contactDetails };

    return callMutationForForm<AdvertLoanMutation>(
      createAdvertLoan({
        variables: {
          input: {
            ...contactField,
            advert: advert.advertId,
            amount: values.amount,
            monthlyPayment: values.monthlyPayment,
            cost: getTotalLoanCost({ ...values, interest: parseFloat(appConfig.advertLoanInterestRate) }),
          },
        },
      }),
    );
  };

  const initialLoanFormValues = { amount: 10000, monthlyPayment: 1000, contactType: 'phone', contactDetails: '' };

  const userTourSteps = [
    {
      target: UserTourSteps.advertPage.adStep1,
      content: t('msg_label_advert_tour_ad_step1'),
    },
    {
      target: UserTourSteps.advertPage.adStep2,
      content: t('msg_label_advert_tour_ad_step2'),
    },
    {
      target: UserTourSteps.advertPage.adStep3,
      content: t('msg_label_advert_tour_ad_step3'),
    },
    {
      target: UserTourSteps.advertPage.adStep4,
      content: t('msg_label_advert_tour_ad_step4'),
    },
  ];

  const appKindCategories = appConfig.parentCategories
    .filter(category => category.kind === appKind)
    .sort((a, b) => a.orderNum - b.orderNum);

  const featuredCategory = appKindCategories.at(0);

  React.useEffect(() => {
    updateAdvertViewsCount({
      variables: {
        input: { id: advert.id },
      },
    });
    if (isFirstLogin && isLoggedIn) {
      dispatch(showUserTour(userTourSteps));
      setToLocalStorage(USER_TOURS.advertPageTour, UserTourStates.off);
    }
    return () => {
      dispatch(showUserTour([]));
    };
  }, []);

  React.useEffect(() => {
    addNewIdToHistory(advert.id);
  }, [advert.id]);

  useSetAppKind(advert.category.kind);

  const sanitizedDescription = advert.description ? xss(advert.description) : '';

  return (
    <GridRow className={styles.advertContentDisplay}>
      <AdvertSchema advert={advert} />
      <GridCol size={12}>
        <Breadcrumb navLinks={buildAdvertBreadCrumbLinks(advert, t, selectTranslation)} />
      </GridCol>
      <GridCol size={12}>
        <PageTitle msg={advert.title} />
      </GridCol>
      <GridCol size={12}>
        <ElementsRow>
          {advert.status === AdvertStatus.Private && (
            <Row alignCenter>
              <Icon icon="lock-filled" size={IconSizes.s} />
            </Row>
          )}
          <AdServiceTypeDisplay
            bold
            dotColor={DotColor.primary}
            color="primary"
            size="s"
            service={advert.serviceType}
            type={advert.advertType}
          />
          <div>
            <Typography tag="span" color="gray" msg={`${advert.viewCount} `} />
            <Typography tag="span" color="gray" msg={t(Messages.adDisplayViewsCount)} />
          </div>
          <div>
            <Typography tag="span" color="gray" msg={t(Messages.labelAdvertId)} />
            <Typography tag="span" color="gray" msg={`: ${advert.advertId}`} />
          </div>
          {isLoggedIn && <AdFavoriteButton favoriteAdvertId={advert.id} isAdFavorited={advert.isFavorited} withLabel />}
        </ElementsRow>
      </GridCol>
      <GridCol size={12}>
        <GridRow>
          <GridCol size={7} className={styles.adOrderForm}>
            <div data-tutorial-step={UserTourSteps.advertPage.adStep1}>
              <Paper width={PaperWidth.full}>
                <PaperBody spacing={PaperBodySpacing.m}>
                  <Row justifyCenter>
                    <Col size={12} className={styles.imagesContainer}>
                      {!advert.mainImage ? (
                        <Image src={PlaceholderImage} className={styles.picture} alt="No image" />
                      ) : (
                        <ImageGallery
                          mainImage={advert.mainImage}
                          mainImageThumbnail={advert.mainImageThumbnail}
                          images={advert.images}
                          mainImageAlt={advert.title}
                        />
                      )}
                    </Col>
                  </Row>
                  <Typography
                    msg={t(Messages.createAdDescriptionField)}
                    tag="h5"
                    size="m"
                    color="grayDark"
                    className={styles.infoSection}
                  />
                  <Row alignCenter constant className={styles.infoSection}>
                    <div dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />
                  </Row>
                </PaperBody>
              </Paper>
            </div>
          </GridCol>
          <GridCol size={5} className={styles.adDescription}>
            <div data-tutorial-step={UserTourSteps.advertPage.adStep1}>
              <Paper width={PaperWidth.full}>
                <PaperBody spacing={PaperBodySpacing.m}>
                  {isAdExpired ? (
                    <Typography tag="div" msg={t(Messages.adExpired)} size="m" className={styles.expired} />
                  ) : (
                    <>
                      {!config.DISABLE_COMPANY_PAGE && (
                        <Row>
                          <Link to={companyRoutePath} className={styles.companyTitleButton}>
                            <UserAvatar thumbnail={advert.company.logo} name={advert.company.name} />
                            <CompanyNameWithRating
                              name={advert.company.name}
                              rating={advert.company.overallRating}
                              small
                            />
                          </Link>
                        </Row>
                      )}
                      <Row alignCenter constant className={styles.infoSection} justifyBetween>
                        <DisplayLabelWithIcon icon="label">
                          <PriceDisplay
                            tag="span"
                            color="gray"
                            priceColor="black"
                            priceBold
                            priceSize="xl"
                            size="s"
                            price={advert.price}
                            pricingType={advert.pricingType}
                          />
                        </DisplayLabelWithIcon>
                      </Row>
                      {advert.address && (
                        <Row constant alignCenter size={10} className={styles.infoSection}>
                          <DisplayLabelWithIcon label={advert.address} icon="location" />
                        </Row>
                      )}
                      <Typography
                        msg={t(Messages.adContactUs)}
                        tag="h5"
                        size="m"
                        color="grayDark"
                        className={styles.infoSection}
                      />
                      <Row alignCenter className={styles.infoSection}>
                        <ButtonLink
                          size={ButtonSizes.fill}
                          label={contactPhone}
                          onClick={e => {
                            e.preventDefault();
                            window.location.href = `tel:${contactPhone}`;
                          }}
                          link="#"
                          className={styles.phoneButton}
                        />
                      </Row>
                      <Row alignCenter className={styles.infoSection}>
                        {isOrderSectionOpen ? (
                          <AdPageOrderSection advert={advert} onCancel={() => setIsOrderSectionOpen(false)} />
                        ) : (
                          <Button
                            onClick={() => setIsOrderSectionOpen(true)}
                            style={ButtonStyles.withIconOutlinedPrimary}
                            size={ButtonSizes.fill}
                            reversed
                            label={t(Messages.openPreliminaryOrder)}
                          >
                            <Icon icon="envelope" size={IconSizes.s} />
                          </Button>
                        )}
                      </Row>
                    </>
                  )}
                </PaperBody>
              </Paper>
            </div>
          </GridCol>
        </GridRow>
      </GridCol>

      <PropertiesTable categoryId={advert.category.id} customFieldsData={advert.customFieldsData} />
      {advert.category.loanCalculatorEnabled && (
        <GridCol size={7}>
          <LoanForm onSubmit={handleSubmit} initialValues={initialLoanFormValues} isSuccess={!!data} />
        </GridCol>
      )}
      <GridCol size={12}>
        <LastPreviewedAdverts itemsCount={4} advertId={advert.id} />
      </GridCol>
      <GridCol size={12}>
        <PageSection title={Messages.relatedAdvertsSectionTitle}>
          <RelatedAdverts itemsCount={4} advertId={advert.id} />
        </PageSection>
      </GridCol>
      <GridCol size={12}>
        <PageSection title={Messages.featuredAdvertsSectionTitle}>
          <FeaturedAdverts itemsCount={4} hideShowMore featuredCategory={featuredCategory} />
        </PageSection>
      </GridCol>
    </GridRow>
  );
};

export default AdPage;
